'use client';
import React, { useState, useEffect } from 'react';
import AppLogo from '@/components/ui/AppLogo';
import { Bell, RefreshCw, Wifi, ChevronDown, Menu, X } from 'lucide-react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';

const navItems = [
  { label: 'Operations', href: '/' },
  { label: 'Sales Engine', href: '/sales-engine' },
  { label: 'Service AI', href: '/service-ai' },
  { label: 'Customer Success', href: '/customer-success' },
  { label: 'Install AI', href: '/install-ai' },
  { label: 'Reporting', href: '/reporting' },
];

export default function AppHeader() {
  const pathname = usePathname();
  const [syncTime, setSyncTime] = useState('');
  const [mobileOpen, setMobileOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);

  useEffect(() => {
    const fmt = () => {
      const now = new Date();
      const h = now.getHours();
      const m = now.getMinutes().toString().padStart(2, '0');
      const s = now.getSeconds().toString().padStart(2, '0');
      const ampm = h >= 12 ? 'PM' : 'AM';
      const h12 = h % 12 || 12;
      setSyncTime(`${h12}:${m}:${s} ${ampm} PT`);
    };
    fmt();
    const interval = setInterval(fmt, 1000);
    return () => clearInterval(interval);
  }, []);

  const notifications = [
    { id: 'n-001', type: 'success', text: 'Estimate #4821 accepted — $18,400 install', time: '2m ago' },
    { id: 'n-002', type: 'warning', text: 'Permit #P-2291 correction required — Anaheim', time: '8m ago' },
    { id: 'n-003', type: 'danger', text: 'Emergency call — No A/C, 108°F, Irvine', time: '14m ago' },
    { id: 'n-004', type: 'info', text: 'AR recovery: $2,840 collected from Martinez', time: '22m ago' },
    { id: 'n-005', type: 'success', text: '6 new maintenance renewals booked', time: '31m ago' },
  ];

  const typeColor: Record<string, string> = {
    success: 'bg-success',
    warning: 'bg-warning',
    danger: 'bg-danger',
    info: 'bg-secondary',
  };

  return (
    <header className="sticky top-0 z-50 w-full">
      {/* Top bar */}
      <div className="bg-secondary px-4 lg:px-8 xl:px-10 2xl:px-16 h-14 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <AppLogo size={32} />
            <span className="font-semibold text-white text-base tracking-tight hidden sm:block">
              Pacific Air<span className="text-accent font-bold">Ops</span>
            </span>
          </div>
          <div className="hidden md:flex items-center gap-1.5 bg-white/10 rounded-full px-3 py-1">
            <span className="w-2 h-2 rounded-full bg-success status-live inline-block" />
            <span className="text-white text-xs font-medium">18 AI Workers Active</span>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <div className="hidden lg:flex items-center gap-1.5 text-white/70 text-xs">
            <Wifi size={12} />
            <span className="font-tabular">Synced {syncTime}</span>
            <RefreshCw size={11} className="text-white/50" />
          </div>

          {/* Notifications */}
          <div className="relative">
            <button
              onClick={() => setNotifOpen(!notifOpen)}
              className="relative p-2 rounded-md hover:bg-white/10 transition-colors"
            >
              <Bell size={18} className="text-white" />
              <span className="absolute top-1 right-1 w-2 h-2 bg-danger rounded-full" />
            </button>
            {notifOpen && (
              <div className="absolute right-0 top-full mt-2 w-80 bg-card rounded-lg card-shadow border border-border z-50 fade-in">
                <div className="px-4 py-3 border-b border-border flex items-center justify-between">
                  <span className="text-sm font-semibold text-foreground">Notifications</span>
                  <span className="text-xs text-muted-foreground">5 new</span>
                </div>
                <div className="divide-y divide-border">
                  {notifications.map((n) => (
                    <div key={n.id} className="px-4 py-3 hover:bg-muted/40 transition-colors cursor-pointer">
                      <div className="flex items-start gap-2.5">
                        <span className={`mt-1 w-2 h-2 rounded-full flex-shrink-0 ${typeColor[n.type]}`} />
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-foreground leading-snug">{n.text}</p>
                          <p className="text-xs text-muted-foreground mt-0.5">{n.time}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="px-4 py-2 border-t border-border">
                  <button className="text-xs text-secondary font-medium hover:underline">View all activity</button>
                </div>
              </div>
            )}
          </div>

          {/* User */}
          <button className="hidden md:flex items-center gap-2 hover:bg-white/10 rounded-md px-2 py-1.5 transition-colors">
            <div className="w-7 h-7 rounded-full bg-primary flex items-center justify-center">
              <span className="text-white text-xs font-semibold">RC</span>
            </div>
            <span className="text-white text-sm font-medium">R. Chen</span>
            <ChevronDown size={14} className="text-white/60" />
          </button>

          <button className="md:hidden p-2 rounded-md hover:bg-white/10 transition-colors" onClick={() => setMobileOpen(!mobileOpen)}>
            {mobileOpen ? <X size={18} className="text-white" /> : <Menu size={18} className="text-white" />}
          </button>
        </div>
      </div>

      {/* Nav tabs */}
      <div className="bg-card border-b border-border shadow-sm">
        <nav className="px-4 lg:px-8 xl:px-10 2xl:px-16 overflow-x-auto scrollbar-thin">
          <div className="flex items-center gap-0 min-w-max">
            {navItems.map((item) => {
              const isActive = item.href === '/' ? pathname === '/' : pathname.startsWith(item.href);
              return (
                <Link
                  key={`nav-${item.href}`}
                  href={item.href}
                  className={`px-4 py-3 text-sm font-medium whitespace-nowrap transition-all border-b-2 ${
                    isActive
                      ? 'border-secondary text-secondary' :'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                  }`}
                >
                  {item.label}
                </Link>
              );
            })}
          </div>
        </nav>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="md:hidden bg-card border-b border-border shadow-lg fade-in">
          <nav className="px-4 py-2 flex flex-col gap-1">
            {navItems.map((item) => {
              const isActive = item.href === '/' ? pathname === '/' : pathname.startsWith(item.href);
              return (
                <Link
                  key={`mobile-nav-${item.href}`}
                  href={item.href}
                  onClick={() => setMobileOpen(false)}
                  className={`px-3 py-2.5 text-sm font-medium rounded-md transition-colors ${
                    isActive ? 'bg-secondary/10 text-secondary' : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                  }`}
                >
                  {item.label}
                </Link>
              );
            })}
          </nav>
        </div>
      )}
    </header>
  );
}